import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ThemeManager } from '../../../../../themes/theme-manager';
import { DateTimePickerDialog } from '../../../../date-time-picker-dialog/date-time-picker-dialog';
import { ContextMenuItem, SkyViewContextMenu } from '../../../../skyview/context-menu';
import { useAssetFilterStore } from '../../../state/asset-filter-store';
import { CreatedByDialog } from '../../dialogs/created-by-dialog/created-by-dialog';
import { ObjectTypeDialog } from '../../dialogs/object-type-dialog/object-type-dialog';
import { TagFilterDialog } from '../../dialogs/tag-filter-dialog/tag-filter-dialog';
import { TimelineFilterDialog } from '../../dialogs/timeline-filter-dialog/timeline-filter-dialog';

// Define Point2d locally to avoid import issues
type Point2d = { x: number; y: number };

export type AssetFilterProps = {
  onClick?: () => void;
  onMenuClose?: () => void;
  openMenuAt?: Point2d | null;
  /** When the menu is opened by a trigger outside this component (e.g. FilterDialog + button), pass its ref so clicks on it don't close the menu. */
  openMenuTriggerRef?: React.RefObject<HTMLElement | null>;
  title?: string;
};

const AssetFilter: React.FC<AssetFilterProps> = ({
  openMenuAt,
  onMenuClose,
  openMenuTriggerRef,
}) => {
  const { t } = useTranslation('skyviewAssetMenu');
  const {
    applyCreatedAtFilter,
    applyImageCapturedAtFilter,
    applyCreatedByFilter,
    applyAssetTypeFilter,
    applyTagGroupsFilter,
    applyTimelineFilter,
  } = useAssetFilterStore();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<Point2d>({ x: 0, y: 0 });
  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);
  const [isCapturedDialogOpen, setIsCapturedDialogOpen] = useState(false);
  const [isCreatedByDialogOpen, setIsCreatedByDialogOpen] = useState(false);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [isTimelineDialogOpen, setIsTimelineDialogOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isMobaTheme = ThemeManager.instance.isMobaTheme();

  const handleClickOutside = React.useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedButton = buttonRef.current?.contains(target);
      const clickedTrigger = openMenuTriggerRef?.current?.contains(target);
      const clickedMenu =
        menuRef.current?.contains(target) ||
        (target instanceof Element &&
          target.closest('[data-skyview-context-menu="true"]') !== null);
      if (clickedButton || clickedTrigger || clickedMenu) {
        return;
      }
      setIsDropdownVisible(false);
      onMenuClose?.();
    },
    [onMenuClose, openMenuTriggerRef],
  );

  React.useEffect(() => {
    if (isDropdownVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownVisible, handleClickOutside]);

  // Open menu programmatically at provided position; close when anchor is cleared (e.g. menu dismissed)
  React.useEffect(() => {
    if (openMenuAt && typeof openMenuAt.x === 'number' && typeof openMenuAt.y === 'number') {
      setDropdownPosition(openMenuAt);
      setIsDropdownVisible(true);
    } else {
      // When openMenuAt is cleared (onClose from RootMenu), unmount so next open gets a fresh menu with isOpen true
      setIsDropdownVisible(false);
    }
  }, [openMenuAt]);

  const filterMenuItems: ContextMenuItem[] = [
    {
      title: t('topArea.filter.created', { ns: 'skyviewAssetMenu' }),
      action: () => undefined,
      children: [
        {
          title: t('topArea.filter.today', { ns: 'skyviewAssetMenu' }),
          action: () => {
            const to = new Date();
            const from = new Date();
            from.setHours(0, 0, 0, 0);
            to.setHours(23, 59, 59, 999);
            applyCreatedAtFilter({
              createdAtFrom: from.toISOString(),
              createdAtTo: to.toISOString(),
            });
            setIsDropdownVisible(false);
            onMenuClose?.();
          },
        },
        {
          title: t('topArea.filter.last7Days', { ns: 'skyviewAssetMenu' }),
          action: () => {
            const to = new Date();
            const from = new Date();
            from.setHours(0, 0, 0, 0);
            to.setHours(23, 59, 59, 999);
            from.setDate(to.getDate() - 7);
            applyCreatedAtFilter({
              createdAtFrom: from.toISOString(),
              createdAtTo: to.toISOString(),
            });
            setIsDropdownVisible(false);
            onMenuClose?.();
          },
        },
        {
          title: t('topArea.filter.last30Days', { ns: 'skyviewAssetMenu' }),
          action: () => {
            const to = new Date();
            const from = new Date();
            from.setHours(0, 0, 0, 0);
            to.setHours(23, 59, 59, 999);
            from.setDate(to.getDate() - 30);
            applyCreatedAtFilter({
              createdAtFrom: from.toISOString(),
              createdAtTo: to.toISOString(),
            });
            setIsDropdownVisible(false);
            onMenuClose?.();
          },
        },
        {
          title: t('topArea.filter.customRange', { ns: 'skyviewAssetMenu' }),
          action: () => {
            setIsDropdownVisible(false);
            setIsCustomDialogOpen(true);
            onMenuClose?.();
          },
        },
      ],
    },
    {
      title: t('topArea.filter.photosCaptured', { ns: 'skyviewAssetMenu' }),
      action: () => undefined,
      children: [
        {
          title: t('topArea.filter.today', { ns: 'skyviewAssetMenu' }),
          action: () => {
            const to = new Date();
            const from = new Date();
            from.setHours(0, 0, 0, 0);
            to.setHours(23, 59, 59, 999);
            applyImageCapturedAtFilter({
              imageCapturedAtFrom: from.toISOString(),
              imageCapturedAtTo: to.toISOString(),
            });
            setIsDropdownVisible(false);
            onMenuClose?.();
          },
        },
        {
          title: t('topArea.filter.last7Days', { ns: 'skyviewAssetMenu' }),
          action: () => {
            const to = new Date();
            const from = new Date();
            from.setHours(0, 0, 0, 0);
            to.setHours(23, 59, 59, 999);
            from.setDate(to.getDate() - 7);
            applyImageCapturedAtFilter({
              imageCapturedAtFrom: from.toISOString(),
              imageCapturedAtTo: to.toISOString(),
            });
            setIsDropdownVisible(false);
            onMenuClose?.();
          },
        },
        {
          title: t('topArea.filter.last30Days', { ns: 'skyviewAssetMenu' }),
          action: () => {
            const to = new Date();
            const from = new Date();
            from.setHours(0, 0, 0, 0);
            to.setHours(23, 59, 59, 999);
            from.setDate(to.getDate() - 30);
            applyImageCapturedAtFilter({
              imageCapturedAtFrom: from.toISOString(),
              imageCapturedAtTo: to.toISOString(),
            });
            setIsDropdownVisible(false);
            onMenuClose?.();
          },
        },
        {
          title: t('topArea.filter.customRange', { ns: 'skyviewAssetMenu' }),
          action: () => {
            setIsDropdownVisible(false);
            setIsCapturedDialogOpen(true);
            onMenuClose?.();
          },
        },
      ],
    },
    {
      title: t('topArea.filter.createdBy', { ns: 'skyviewAssetMenu' }),
      action: () => {
        setIsDropdownVisible(false);
        setIsCreatedByDialogOpen(true);
        onMenuClose?.();
      },
    },
    {
      title: t('topArea.filter.tags', { ns: 'skyviewAssetMenu' }),
      action: () => {
        setIsDropdownVisible(false);
        setIsTagDialogOpen(true);
        onMenuClose?.();
      },
    },
  ];

  if (!isMobaTheme) {
    filterMenuItems.push({
      title: t('timeline.sectionName', { ns: 'skyviewAssetMenu' }),
      action: () => {
        setIsDropdownVisible(false);
        setIsTimelineDialogOpen(true);
        onMenuClose?.();
      },
    });
  }

  filterMenuItems.push({
    title: t('topArea.filter.type', { ns: 'skyviewAssetMenu' }),
    action: () => {
      setIsDropdownVisible(false);
      setIsTypeDialogOpen(true);
      onMenuClose?.();
    },
  });

  return (
    <>
      {isDropdownVisible && (
        <div ref={menuRef}>
          <SkyViewContextMenu items={filterMenuItems} pos={dropdownPosition} />
        </div>
      )}

      {isCustomDialogOpen && (
        <DateTimePickerDialog
          displayTime={false}
          endDate={new Date()}
          maxDate={new Date()}
          range={true}
          startDate={new Date()}
          onCancel={() => setIsCustomDialogOpen(false)}
          onDateChoosen={() => {
            // not used in range mode
          }}
          onRangeChoosen={(from, to) => {
            const startOfDay = new Date(from);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(to);
            endOfDay.setHours(23, 59, 59, 999);
            applyCreatedAtFilter({
              createdAtFrom: startOfDay.toISOString(),
              createdAtTo: endOfDay.toISOString(),
            });
            setIsCustomDialogOpen(false);
          }}
        />
      )}

      {isCapturedDialogOpen && (
        <DateTimePickerDialog
          displayTime={false}
          endDate={new Date()}
          maxDate={new Date()}
          range={true}
          startDate={new Date()}
          onCancel={() => setIsCapturedDialogOpen(false)}
          onDateChoosen={() => {
            // not used in range mode
          }}
          onRangeChoosen={(from, to) => {
            const startOfDay = new Date(from);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(to);
            endOfDay.setHours(23, 59, 59, 999);
            applyImageCapturedAtFilter({
              imageCapturedAtFrom: startOfDay.toISOString(),
              imageCapturedAtTo: endOfDay.toISOString(),
            });
            setIsCapturedDialogOpen(false);
          }}
        />
      )}

      {isCreatedByDialogOpen && (
        <CreatedByDialog
          onClose={(user) => {
            if (user?.id) {
              applyCreatedByFilter({ id: user.id, title: user.email });
            }
            setIsCreatedByDialogOpen(false);
          }}
        />
      )}

      {isTagDialogOpen && (
        <TagFilterDialog
          onClose={(tagGroups) => {
            if (tagGroups && tagGroups.length > 0) {
              applyTagGroupsFilter(tagGroups);
            }
            setIsTagDialogOpen(false);
          }}
        />
      )}

      {isTypeDialogOpen && (
        <ObjectTypeDialog
          onClose={(type) => {
            if (type) {
              applyAssetTypeFilter(type);
            }
            setIsTypeDialogOpen(false);
          }}
        />
      )}

      {isTimelineDialogOpen && (
        <TimelineFilterDialog
          onClose={(timeline) => {
            if (timeline?.id) {
              applyTimelineFilter(timeline.id);
            }
            setIsTimelineDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

export { AssetFilter };
